<script setup lang="ts">
import type { Menu } from "@mmh/types"
import { Menu as MenuIcon } from "lucide-vue-next"
import { computed } from "vue"
import { useRoute } from "vue-router"
import { ReservationDialogV2 } from "~/components/reservation"
import { ThemeButton } from "~/components/theme"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { useMedia } from "~/composables/media"
import { scrollToAndFocus, useScrolled } from "~/composables/scroll"
import { titleCase } from "~/lib/case"
import { cn } from "~/lib/utils"
import { NavItem, NavLogo, NavMenu, NavSidebar } from "."

const route = useRoute()
const { isDesktop } = useMedia()
const { concept } = useConcept()
const { location, hasOneLocation } = useLocation()
const scrolled = useScrolled()

const hasContent = (key: string): boolean => {
    // TODO: Handle the closed case differently?
    if (location.value?.closed) return false
    if (!location.value) return false
    
    if (key === "featuredSection") return location.value?.pageContent?.featuredSection?.visible !== false
    if (key === "largePartyReservations") return location.value?.pageContent?.largePartyReservations?.visible !== false
    if (key === "privateDining") return location.value?.pageContent?.privateDining?.visible !== false
    if (key === "catering") return location.value?.pageContent?.catering?.visible !== false
    if (key === "merch") return !!concept.value?.merch
    if (key === "hospitalityGroup") return location.value?.pageContent?.hospitalityGroup?.visible !== false
    if (key === "giftCardsRewards") return location.value?.pageContent?.giftCardsRewards?.visible !== false
    
    if (key === "menus") {
        const hasMenus = (Object.values(location.value?.menus || {}) as Menu[][]).some(arr => arr.length > 0)
        const isVisible = location.value?.pageContent?.menus?.visible !== false
        return isVisible && hasMenus
    }
    
    // Default: check if data exists
    return !!location.value.pageContent?.[key as keyof typeof location.value.pageContent]
}

const centerMenuItems = computed<NavItem[]>(() => {
    // TODO: Handle the closed case (Jemma Pizzeria) differently?
    if (location.value?.closed) return []
    if (route.name === "concept" || (!route.params.name && !hasOneLocation.value)) return []
    if (oneLocationRouteSlug.value) return []
    
    const locationPath = hasOneLocation.value ? "" : `/${route.params.name}`
    
    const subItems = [
        hasContent("merch") && {
            name: "Merch",
            to: `${locationPath}/merch`
        },
        hasContent("hoursLocationContact") && {
            name: "Hours & Location",
            to: `${locationPath}/hours-location`
        },
        hasContent("hospitalityGroup") && {
            name: titleCase(concept.value.parentCompany.dba),
            to: { path: locationPath || "/", hash: "#hospitality-group" }
        },
        hasContent("giftCardsRewards") && {
            name: "Gift Cards & Rewards",
            to: `${locationPath}/gift-cards-rewards`
        },
        hasContent("faqs") && {
            name: "FAQs",
            to: `${locationPath}/faq`
        }
    ].filter(Boolean) as NavItem[]
    
    const mainItems: NavItem[] = [
        hasContent("menus") && {
            name: "Menus",
            to: `${locationPath}/menus/daily`
        },
        hasContent("largePartyReservations") && {
            name: "Large Party Reservations",
            to: `${locationPath}/large-party-reservations`
        },
        hasContent("privateDining") && {
            name: "Private Dining",
            to: `${locationPath}/private-dining`
        },
        hasContent("catering") && {
            name: "Catering",
            to: `${locationPath}/catering`
        }
    ].filter(Boolean) as NavItem[]
    
    if (mainItems.length < 4 && subItems.length > 0) {
        mainItems.push(subItems[0])
        
        if (subItems.length > 1) {
            mainItems.push({ 
                name: "More", 
                items: subItems.slice(1) 
            })
        }
    } else if (subItems.length > 0) {
        mainItems.push({ 
            name: "More", 
            items: subItems 
        })
    }
    
    return mainItems
})

const endMenuItems = computed<NavItem[]>(() => {
    // TODO: Handle the closed case (Jemma Pizzeria) differently?
    if (location.value?.closed) return []
    const items: NavItem[] = []
    if (route.name === "concept") {
        items.push({
            name: "Locations",
            to: "#locations",
            button: true
        })
    } else if (location.value) {
        items.push({
            name: !isDesktop.value ? "Order" : "Order Online",
            to: location.value?.external?.url?.onlineOrdering || "https://mmh.co/",
            button: true
        })
    }
    const basePath = route.path === "/" ? "" : route.path
    const locationPath = hasOneLocation.value ? "" : (route.params.name ? `/${route.params.name}` : basePath)
    
    const reservationItem: NavItem = {
        name: "Reservations",
        modal: {
            component: ReservationDialogV2
        },
        button: true,
        emphasize: true,
        class: "hidden md:flex"
    }

    if (location.value) {
        reservationItem.to = `${locationPath}/reservations`
    }

    items.push(reservationItem)

    return items
})

const sideMenuItems = computed<NavItem[]>(() => {
    // TODO: Handle the closed case (Jemma Pizzeria) differently?
    if (location.value?.closed) return []
    if (route.name === "concept" || (!route.params.name && !hasOneLocation.value)) return []
    if (oneLocationRouteSlug.value) return []
    
    const locationPath = hasOneLocation.value ? "" : `/${route.params.name}`
    
    const items: NavItem[] = [
        {
            name: "Order Online",
            to: location.value?.external?.url?.onlineOrdering
        },
        hasContent("menus") && {
            name: "Menus",
            to: `${locationPath}/menus/daily`
        },
        hasContent("largePartyReservations") && {
            name: "Large Party Reservations",
            to: `${locationPath}/large-party-reservations`
        }, 
        hasContent("privateDining") && {
            name: "Private Dining",
            to: `${locationPath}/private-dining`
        },
        hasContent("catering") && {
            name: "Catering",
            to: `${locationPath}/catering`
        },
        hasContent("merch") && {
            name: "Merch",
            to: `${locationPath}/merch`
        },
        hasContent("hoursLocationContact") && {
            name: "Hours & Location",
            to: `${locationPath}/hours-location`
        },
        hasContent("hospitalityGroup") && {
            name: titleCase(concept.value.parentCompany.dba),
            to: { path: locationPath || "/", hash: "#hospitality-group" }
        },
        hasContent("giftCardsRewards") && {
            name: "Gift Cards & Rewards",
            to: `${locationPath}/gift-cards-rewards`
        },
        hasContent("faqs") && {
            name: "FAQs",
            to: `${locationPath}/faq`
        }
    ].filter(Boolean) as NavItem[]
    
    return items
})

const oneLocationRouteSlug = computed(() => {
    const legalPages = ["privacy-policy", "terms-of-use", "accessibility-statement"]
    return hasOneLocation.value && legalPages.includes(route.name as string)
})

function handleNavSelect(sectionId?: string) {
    if (sectionId) scrollToAndFocus(sectionId)
}
</script>

<template>
    <header
        id="nav-topbar"
        tabindex="-1"
        :class="cn(
            'min-h-32 w-full fixed top-0 flex items-center justify-between px-3.5 md:px-6 z-50 transition-[min-height,background-color,color] duration-500 ease-in-out will-change-transform text-nav-bar-items focus-visible:outline-none', 
            (scrolled || oneLocationRouteSlug) && 'min-h-16 md:min-h-14 bg-nav-bar-background-scrolled text-nav-bar-text-scrolled shadow-lg'
        )"
    >
        <div
            class="w-full flex items-center justify-between transition-all duration-500 ease-in-out"
        >
            <div class="w-32 flex justify-start">
                <NavLogo />
            </div>
            <NavMenu
                :items="centerMenuItems"
                aria-label="Main page navigation"
                class="hidden xl:flex"
                @select="handleNavSelect"
            />
            <div class="flex items-center justify-end min-w-0 gap-1">
                <NavMenu
                    :items="endMenuItems"
                    aria-label="Order online and reservations"
                    @select="handleNavSelect"
                /> 
                <div class="block xl:hidden">
                    <NavSidebar
                        :items="sideMenuItems"
                        side="right"
                    >
                        <ThemeButton
                            variant="ghost"
                            size="icon"
                            tabindex="0"
                            class="size-9 p-0 [&_svg]:size-7 hover:bg-primary hover:text-primary-foreground text-nav-bar-sidebar-text"
                            aria-label="Toggle navigation menu"
                        >
                            <MenuIcon />
                            <span class="sr-only">Toggle navigation menu</span>
                        </ThemeButton>
                    </NavSidebar>
                </div> 
            </div>
        </div> 
    </header>
</template>